import { type NuxtAxiosInstance } from '@/types/axios'
import { PaymentMethodsType, type StripePromptPayResult, type payByTngPayload } from '@/view-models/payment'
import { PlatformSource } from '@/view-models/platform'
import { type ResultGenericVM } from '@/view-models/common'
import { addEndpointPrefix } from '@/utils/apiAdapter'
import { querySerializer } from '@/utils/common'
import { TapPaySDK } from '@/utils/payment/payment'

type PaymentServiceMap = {
  [PaymentMethodsType.ATMToMerchant]: typeof payByAtm
  [PaymentMethodsType.PromptPay]: typeof createPromptPayPayment
  [PaymentMethodsType.LINEPay]: typeof createTapPayThirdPartyPayment
  [PaymentMethodsType.AFTEE]: typeof createTapPayThirdPartyPayment
}

type PaymentMethodImplementationKey = PaymentMethodsType.ATMToMerchant | PaymentMethodsType.PromptPay | TapPayThirdPartyMethodType

type TapPayThirdPartyMethodType = PaymentMethodsType.LINEPay | PaymentMethodsType.AFTEE

/**
 * API: https://github.com/funnow-docs/funnow-api-doc/blob/b662972c1ef753209d57626b48d12874e0387c6b/docs/payment/atm_to_merchant_payment.md#%E7%B5%90%E5%B8%B3%E6%B5%81%E7%A8%8B
 *
 * @description orderid string 訂單 ID
 * from string 請求 frontend 來源, michelin, cathay, booking_engine
 * amount number 交易金額 比對價格用，FunNow 並不會直接收到這筆錢
 *
 */
export function createPayment<T extends PaymentMethodImplementationKey>(
  axios: NuxtAxiosInstance,
  params: { methodType: T; payload: any }
): Promise<ReturnType<PaymentServiceMap[T]>> {
  const payload = adaptPaymentPayload(params.methodType, params.payload)

  const paymentServiceMap: PaymentServiceMap = {
    [PaymentMethodsType.ATMToMerchant]: payByAtm,
    [PaymentMethodsType.PromptPay]: createPromptPayPayment,
    [PaymentMethodsType.LINEPay]: createTapPayThirdPartyPayment,
    [PaymentMethodsType.AFTEE]: createTapPayThirdPartyPayment,
  }

  const serviceFn = paymentServiceMap[params.methodType]

  if (!serviceFn) {
    throw new Error(`payment gateway not found: ${params.methodType}`)
  }

  return serviceFn(axios, payload, params.methodType as TapPayThirdPartyMethodType)
}

// 如果有 ATM 以外的付款方式要用這個 function，要再盤點一下 payload
function adaptPaymentPayload(methodType: PaymentMethodsType, payload: any) {
  const {
    orderID: orderid,
    amount,
    bonus,
    prime,
    promoUsage: promo_usage,
    from = null,
    productAmount: product_amount,
    promoCodeID: code_id,
    token,
  } = payload

  const result = {
    orderid,
    amount,
    from,
    bonus,
    product_amount,
    prime,
    code_id,
    promo_usage,
    is_new_card: false,
    bin: undefined,
    bin_code_id: undefined,
    token,
  }

  if (methodType === PaymentMethodsType.NewCreditCard) {
    result.is_new_card = true
    result.bin = payload.bin
    result.bin_code_id = payload.binCodeID
  }

  if (methodType === PaymentMethodsType.LINEPay || methodType === PaymentMethodsType.AFTEE) {
    return Object.assign(result, adaptRequiredPaymentParams(payload))
  }

  return result
}

async function payByAtm(axios: NuxtAxiosInstance, payload: { orderid: string; from: string; amount: number; token?: string }) {
  try {
    const apiPrefix = addEndpointPrefix(!!payload?.token)
    const endPoint = apiPrefix + '/payment/atm-to-merchant'
    const result = await axios.$post(endPoint, payload)
    return result
  } catch (error) {
    console.error('pay by ATM API error: ', error)
    return error
  }
}
/** mini program 不會走 funbook 流程，所以不需要 public */
export async function payByTng(axios: NuxtAxiosInstance, { orderid, amount, bonus, promo_usage, code_id }: payByTngPayload) {
  const endPoint = '/payment/tng'
  const result = await axios.$post(endPoint, { orderid, amount, bonus, promo_usage, code_id })
  return result
}

export async function createPromptPayPayment(
  axios: NuxtAxiosInstance,
  payload: ReturnType<typeof adaptPaymentPayload>
): Promise<ResultGenericVM<StripePromptPayResult>> {
  try {
    const { orderid, amount, bonus, code_id, promo_usage, token } = payload
    const apiPrefix = addEndpointPrefix(!!token)
    const endPoint = apiPrefix + '/payment/stripe/promptpay'

    const result = await axios.$post<ResultGenericVM<StripePromptPayResult>>(endPoint, {
      orderid,
      amount,
      bonus,
      code_id,
      promo_usage,
      token,
    })

    return result
  } catch (error) {
    console.error('pay by ATM API error: ', error)
    throw error
  }
}

async function createTapPayThirdPartyPayment(axios: NuxtAxiosInstance, paymentData: any, methodType: TapPayThirdPartyMethodType) {
  const prime = await getTapPayThirdPartyPrime(methodType)
  return payByTapPayThirdParty(axios, { ...paymentData, prime }, methodType)
}

export async function confirmGrabPay(axios: NuxtAxiosInstance, { orderID, authCode, token }: { orderID: number; authCode: string; token?: string }) {
  try {
    const endPoint = `${addEndpointPrefix(!!token)}/payment/grabpay/confirm`
    const res = await axios.$post(endPoint, {
      orderid: orderID,
      auth_code: authCode,
      token,
    })
  } catch (error) {
    console.error('confirmGrabpay:error', error)
    return error
  }
}

export async function getTapPayThirdPartyPrime(methodType: TapPayThirdPartyMethodType) {
  const thirdPartyPrimes = Object.freeze({
    [PaymentMethodsType.LINEPay]: TapPaySDK.getLinePayPrime,
    [PaymentMethodsType.AFTEE]: TapPaySDK.getAfteePrime,
  })

  const primePayload = await thirdPartyPrimes[methodType]()

  return primePayload.prime
}

export function payByTapPayThirdParty(axios: NuxtAxiosInstance, paymentData: any, methodType: PaymentMethodsType.LINEPay | PaymentMethodsType.AFTEE) {
  const prefix = addEndpointPrefix(!!paymentData?.token)
  let endpoint = '/payment/getPaymentURL'
  switch (methodType) {
    case PaymentMethodsType.LINEPay:
      endpoint = '/payment/getPaymentURL'
      break
    case PaymentMethodsType.AFTEE:
      endpoint = '/payment/get-aftee-payment-url'
      break
  }
  const apiUrl = prefix + endpoint

  return axios.$post(apiUrl, paymentData)
}

/**
 *
 * @link https://github.com/funnow-docs/funnow-api-doc/blob/master/docs/payment/gateway.md#%E4%BB%98%E6%AC%BE%E9%A0%81
 */

export async function getPaymentGateway(axios: NuxtAxiosInstance, pid: number, query: { from: PlatformSource; t?: string; code_ids?: number[] }) {
  const apiPrefix = addEndpointPrefix(!!query?.t)

  const endPoint = apiPrefix + `/product/${pid}/gateway?${querySerializer(query)}`

  try {
    const res = await axios.$get(endPoint, { headers: { APIVer: 2 } })
    if (res.code !== 0) {
      throw res
    }

    return res
  } catch (error) {
    console.error('getPaymentGateway:error', error)
    return error
  }
}

export async function getIPay88Banks(axios: NuxtAxiosInstance) {
  try {
    const res = await axios.$get('payment/ipay88/banks')

    return res
  } catch (error) {
    console.error('getIPay88Banks:error', error)
    return []
  }
}

export async function getFunCoinsInfo(axios: NuxtAxiosInstance, { currencyId }: { currencyId: number }) {
  try {
    const res = await axios.$get('member/funcoin', { params: { currency_id: currencyId } })

    return res
  } catch (error) {
    console.error('getFunCoinsInfo:error', error)
    return error
  }
}

/**
 * 驗證信用卡持卡人資訊
 * API: PATCH /payment/credit-cards/:card_id
 * @param axios NuxtAxiosInstance
 * @param cardId 信用卡 ID
 * @param data 持卡人驗證資料 { name, phone, email }
 */
export async function verifyCreditCardHolder(axios: NuxtAxiosInstance, cardId: number, data: { name: string; phone: string; email: string }) {
  const endpoint = `/payment/credit-cards/${cardId}`
  const res = await axios.$patch<ResultGenericVM<any>>(endpoint, data)

  if (res.code !== 0) {
    throw res
  }

  return res
}

export function adaptRequiredPaymentParams(param: any) {
  const { orderID, amount, bonus, productAmount, from } = param
  return {
    orderid: orderID,
    amount: amount,
    bonus: bonus,
    product_amount: productAmount,
    from: from || null,
  }
}
